// The view one `ModelPicker` renders (#5210, epic #5200) over the domain's option list.
//
// `modelPickerOptions` (`@developerz/domain`, #5201) owns WHICH models a purpose's
// picker offers and in what order — through the resolver's own lane fence and parked
// predicates. This file owns nothing of that: it keeps the domain's order, drops what
// the typed filter does not match, splits the list into the sections the picker draws
// (Automatic and the current pin first, then provider groups, then the collapsed
// "Unavailable"), and names which rows the arrow keys may land on. Pure — no i18n,
// no DOM; the caller passes the one label it filters Automatic by.

import type {
  ModelOffer,
  ModelPickerOption,
  RouteChange,
  UnavailableOffer,
} from '@developerz/domain';

export type PickerRow =
  | { readonly kind: 'automatic'; readonly id: 'automatic'; readonly current: boolean }
  | {
      readonly kind: 'model';
      readonly id: string;
      readonly current: boolean;
      readonly offer: ModelOffer;
    }
  | {
      readonly kind: 'unavailable';
      readonly id: string;
      readonly current: boolean;
      readonly offer: UnavailableOffer;
    }
  | {
      readonly kind: 'gone';
      readonly id: string;
      readonly current: true;
      readonly provider: string | null;
      readonly model: string;
      readonly displayName: string;
    };

/** A row the picker lets a person choose. */
export type SelectableRow = Extract<PickerRow, { kind: 'automatic' | 'model' }>;

export type PickerView = {
  /** Every rendered row, in the domain's order. */
  readonly rows: readonly PickerRow[];
  /** Automatic and the current pin (when there is one). */
  readonly head: readonly PickerRow[];
  /** The other selectable models, grouped by provider in order of appearance. */
  readonly groups: readonly {
    readonly provider: string;
    readonly rows: readonly Extract<PickerRow, { kind: 'model' }>[];
  }[];
  /** Models only unverified or invalid keys serve — shown collapsed, never chosen. */
  readonly unavailable: readonly Extract<PickerRow, { kind: 'unavailable' }>[];
  /** What the arrow keys walk, in render order. */
  readonly selectable: readonly SelectableRow[];
  /** Where the cursor starts: the current pin, else the first row. */
  readonly initial: number;
  /** No key serves any model for this purpose. */
  readonly empty: boolean;
  /** A filter is typed and matches nothing. */
  readonly noMatch: boolean;
};

/** A stable identity per option — the picker's `data-option`, and what AC1 compares. */
export function optionId(option: ModelPickerOption): string {
  switch (option.kind) {
    case 'automatic':
    case 'empty':
      return option.kind;
    case 'pinned':
      return option.gone
        ? `${option.provider ?? '*'}/${option.model}`
        : `${option.offer.provider}/${option.offer.model}`;
    case 'model':
    case 'unavailable':
      return `${option.provider}/${option.model}`;
  }
}

function rowOf(option: ModelPickerOption): PickerRow | null {
  const id = optionId(option);
  switch (option.kind) {
    case 'empty':
      return null;
    case 'automatic':
      return { kind: 'automatic', id: 'automatic', current: false };
    case 'model':
      return { kind: 'model', id, current: false, offer: option };
    case 'unavailable':
      return { kind: 'unavailable', id, current: false, offer: option };
    case 'pinned':
      if (option.gone) {
        const { provider, model, displayName } = option;
        return { kind: 'gone', id, current: true, provider, model, displayName };
      }
      return option.offer.kind === 'model'
        ? { kind: 'model', id, current: true, offer: option.offer }
        : { kind: 'unavailable', id, current: true, offer: option.offer };
  }
}

function haystack(row: PickerRow, automaticLabel: string): string {
  switch (row.kind) {
    case 'automatic':
      return automaticLabel;
    case 'gone':
      return `${row.displayName} ${row.model} ${row.provider ?? ''}`;
    default:
      return `${row.offer.displayName} ${row.offer.model} ${row.offer.provider}`;
  }
}

export function pickerView(
  options: readonly ModelPickerOption[],
  query: string,
  automaticLabel: string,
): PickerView {
  const needle = query.trim().toLowerCase();
  const all = options.map(rowOf).filter((r): r is PickerRow => r !== null);
  const pinned = all.some((r) => r.current);
  const rows = all
    .map((r) => (r.kind === 'automatic' && !pinned ? { ...r, current: true } : r))
    .filter((r) => needle === '' || haystack(r, automaticLabel).toLowerCase().includes(needle));

  const head = rows.filter((r) => r.kind === 'automatic' || r.current);
  const groups: { provider: string; rows: Extract<PickerRow, { kind: 'model' }>[] }[] = [];
  for (const r of rows) {
    if (r.kind !== 'model' || r.current) continue;
    const last = groups.at(-1);
    if (last?.provider === r.offer.provider) last.rows.push(r);
    else groups.push({ provider: r.offer.provider, rows: [r] });
  }
  const unavailable = rows.filter(
    (r): r is Extract<PickerRow, { kind: 'unavailable' }> => r.kind === 'unavailable' && !r.current,
  );
  const selectable = [...head, ...groups.flatMap((g) => g.rows)].filter(
    (r): r is SelectableRow => r.kind === 'automatic' || r.kind === 'model',
  );
  const initial = Math.max(
    0,
    selectable.findIndex((r) => r.current),
  );
  return {
    rows,
    head,
    groups,
    unavailable,
    selectable,
    initial,
    empty: options.some((o) => o.kind === 'empty'),
    noMatch: needle !== '' && rows.length === 0,
  };
}

/** The pin change choosing a row makes. */
export function changeFor(row: SelectableRow): RouteChange {
  return row.kind === 'automatic'
    ? { kind: 'automatic' }
    : { kind: 'model', provider: row.offer.provider, model: row.offer.model };
}
